import { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';

// JWT tokens have exactly 3 parts: header.payload.signature
const JWT_PARTS_COUNT = 3;

// Module-level cache: survives component remounts within the same browser
// session. Backstage entity pages can remount during entity re-fetches, which
// would reset useState('') and briefly hide owner-gated UI. Seeding useState
// from this cache means the resolved email is available synchronously on every
// remount, eliminating the flash-then-disappear behavior.
let resolvedEmail: string = '';

/**
 * Hook to get the current user's email from the identity API.
 *
 * Strategy (in order):
 *  1. getProfileInfo() — works for all auth providers. Backstage's guest auth
 *     returns 'guest@example.com' as a placeholder; that is remapped to
 *     'guest@wellsky.com' to match the backend's entity-ref derivation.
 *  2. JWT token payload.email (non-@example.com) — for providers that embed
 *     the real email in the token but not in profile.
 *  3. JWT sub claim → localPart@wellsky.com.
 *  4. getBackstageIdentity() → userEntityRef → localPart@wellsky.com.
 */
export const useCurrentUserEmail = (): string => {
  const identityApi = useApi(identityApiRef);
  // Seed from module-level cache so remounts don't briefly return ''.
  const [currentUserEmail, setCurrentUserEmail] =
    useState<string>(resolvedEmail);

  useEffect(() => {
    // If already resolved (cached from a previous mount), skip the async fetch.
    if (resolvedEmail) {
      setCurrentUserEmail(resolvedEmail);
      return;
    }

    const fetchUserEmail = async () => {
      try {
        // Primary: getProfileInfo() works for all auth providers.
        // Backstage guest auth returns 'guest@example.com' as a placeholder.
        // Remap *@example.com → *@wellsky.com so the local part is preserved
        // and the derived email matches what the backend writes (guest@wellsky.com
        // derived from the userEntityRef user:default/guest).
        const profile = await identityApi.getProfileInfo();
        if (profile?.email) {
          const email = profile.email.endsWith('@example.com')
            ? profile.email.replace('@example.com', '@wellsky.com')
            : profile.email;
          resolvedEmail = email;
          setCurrentUserEmail(email);
          return;
        }

        // Fallback: decode JWT. Note: JWT uses base64url encoding
        // (- and _ instead of + and /), so atob() alone is not safe.
        const credentials = await identityApi.getCredentials();
        if (credentials?.token) {
          const tokenParts = credentials.token.split('.');
          if (tokenParts.length === JWT_PARTS_COUNT) {
            // Normalize base64url → base64 before decoding
            const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
            const payload = JSON.parse(atob(base64));

            if (payload.email && !payload.email.endsWith('@example.com')) {
              resolvedEmail = payload.email;
              setCurrentUserEmail(payload.email);
              return;
            } else if (payload.sub) {
              const match = payload.sub.match(/user:(?:default|[^/]+)\/(.+)/);
              if (match?.[1]) {
                const derived = `${match[1]}@wellsky.com`;
                resolvedEmail = derived;
                setCurrentUserEmail(derived);
                return;
              }
            }
          }
        }

        // Final fallback: getBackstageIdentity() works for ALL auth providers
        // including guest (which has no JWT). Derives localPart@wellsky.com
        // from the userEntityRef (e.g. user:default/guest → guest@wellsky.com).
        const identity = await identityApi.getBackstageIdentity();
        if (identity?.userEntityRef) {
          const match = identity.userEntityRef.match(
            /user:(?:default|[^/]+)\/(.+)/,
          );
          if (match?.[1]) {
            const derived = `${match[1]}@wellsky.com`;
            resolvedEmail = derived;
            setCurrentUserEmail(derived);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(
          'useCurrentUserEmail: Unable to get user credentials',
          error,
        );
      }
    };

    fetchUserEmail();
  }, [identityApi]);

  return currentUserEmail;
};
